/*
 * kp-pdp.js — the behaviours the PDP buy box adds that Dawn has no primitive
 * for. The offer panel's <kp-copy-code> is in kp-copy-code.js, shared with
 * the product card's coupon strip; the rest is here.
 *
 * Wrapped in an IIFE: every kp-*.js is a classic <script src>, and a section
 * with a preset can put the same file on a page twice (or share a page with
 * another kp file that names the same top-level const). A bare lexical
 * declaration then throws `already declared` and kills the whole file;
 * inside a function scope it is harmless, and customElements.get() keeps
 * the define idempotent. (Ticket #22.)
 */
(() => {

/*
 * Re-execution, and what it means now that the PDP can swap product in place.
 *
 * The sibling designs row (#48) makes Dawn's Combined-Listings swap path live
 * on this store, and HTMLUpdateUtility.setInnerHTML deliberately RE-INJECTS
 * every <script> inside <main> so the swapped-in page's scripts run. This
 * file is one of them, so its whole body runs again on every swap.
 *
 * The IIFE already stops that throwing. It does NOT stop the side effects:
 * measured across two swaps, the document-level keydown listeners went 0 → 3
 * → 7, and with two copies of the lightbox handler registered ArrowRight
 * stepped the gallery twice.
 *
 * The split below is the fix, and it is a split rather than a blanket guard
 * because the two halves want opposite things:
 *
 *   - DELEGATED document listeners must be registered ONCE. They already
 *     reach the new DOM, because they match on the event's target.
 *   - PER-ELEMENT setup must run AGAIN, for the elements the swap brought in
 *     — but must not attach a second time to elements that already have it.
 *     Both DOMs are alive for 500ms during a swap, so "already has it" is a
 *     real case, not a hypothetical.
 */
const KP_PDP_FIRST_RUN = !window.kpPdpInitialised;
window.kpPdpInitialised = true;

/*
 * <kp-read-more> — the character paragraph, shortened.
 *
 * Three jobs. It hoists the sign-off line clear of the body: every
 * description here ends on one short bold statement, and that is the sentence
 * written to be read — it must not be the one a truncation eats. It moves the
 * tail (the cut mark and the toggle) to the end of the prose, so the way out
 * sits where the sentence runs out rather than as a third line under the
 * sign-off. Then it shortens the text until both fit the clamp.
 *
 * The shortening is a binary search over one text node rather than a CSS
 * clip, because the toggle has to sit INSIDE the last visible line and the
 * page ground is a noise texture — the usual trick, an absolutely positioned
 * toggle over a gradient that fakes the background, has no flat colour here
 * to fake with.
 *
 * The clamp arrives with `.is-ready`, set here. That is the whole no-JS
 * story: no script, no class, no clamp, tail still hidden — the visitor gets
 * the entire description. Nothing this element can hide is unreachable
 * without it. With the script, collapsed text is genuinely out of the DOM,
 * which is the ordinary disclosure contract: the toggle carries
 * aria-expanded and aria-controls, and opening it restores every word.
 *
 * Dawn's product-info.js re-renders only the ids it names (price, inventory,
 * sku, the picker) on a variant change, and the description is not among
 * them — but a custom element costs nothing to be safe about: if it is ever
 * replaced, connectedCallback runs again on the new one.
 */
class KpReadMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.product__description-body');
    this.tail = this.querySelector('.product__description-tail');
    this.toggle = this.querySelector('[data-kp-more]');
    this.cut = this.querySelector('.product__description-cut');
    if (!this.body || !this.tail || !this.toggle) return;

    this.hoistSignoff();

    /* Nothing safe to shorten — a description that is all markup and no plain
     * run — so it is left whole rather than clipped with no way to open it. */
    this.target = this.findTarget();
    if (!this.target) return;

    this.full = this.target.textContent;
    this.textNode = document.createTextNode(this.full);

    /* The words the clamp cuts stay in the document, in a clipped span, rather
     * than living only in `this.full`. A crawler that runs our JS — Google
     * does — would otherwise index two lines of a description we wrote in
     * full, and an agent reading the rendered page would see the same stub.
     * aria-hidden because the visible clamp and the toggle already say it to
     * a screen reader, and the span holds no text the toggle cannot reveal. */
    this.rest = document.createElement('span');
    this.rest.className = 'product__description-rest';
    this.rest.setAttribute('aria-hidden', 'true');

    this.target.textContent = '';
    this.target.append(this.textNode, this.tail, this.rest);
    this.open = false;
    this.classList.add('is-ready');

    this.fit = this.fit.bind(this);
    this.schedule = this.schedule.bind(this);
    /* Orbitron and Rajdhani are self-hosted and swap in late; text measured
     * against the fallback face wraps differently, and the cut would land in
     * the wrong place. */
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(this.fit);
    } else {
      this.fit();
    }
    window.addEventListener('resize', this.schedule, { passive: true });

    this.toggle.addEventListener('click', () => this.setOpen(!this.open));
  }

  disconnectedCallback() {
    if (this.schedule) window.removeEventListener('resize', this.schedule);
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }

  /*
   * The last paragraph whose only element child is a bold run, and whose text
   * is nothing but that run — the same test kp-main-product.css uses to paint
   * it accent, kept in step here. Both halves matter: `<p>Care note:
   * <strong>hand wash only.</strong></p>` fails the second and stays put.
   *
   * querySelectorAll goes to any depth, which the stylesheet's child
   * combinator cannot: a description pasted several <div>s deep gets its
   * sign-off found here even where the CSS-only path misses it.
   */
  hoistSignoff() {
    const paragraphs = this.body.querySelectorAll('p');
    const last = paragraphs[paragraphs.length - 1];
    if (!last || last.children.length !== 1) return;

    const bold = last.children[0];
    if (bold.tagName !== 'STRONG' && bold.tagName !== 'B') return;
    if (last.textContent.trim() !== bold.textContent.trim()) return;

    last.classList.add('product__description-signoff');
    this.insertBefore(last, this.tail);
  }

  /* The last element holding text and no elements of its own. Rewriting its
   * textContent is then lossless — an element with children would lose their
   * markup, so those are skipped rather than flattened. */
  findTarget() {
    const all = this.body.querySelectorAll('*');
    for (let i = all.length - 1; i >= 0; i -= 1) {
      /* The spec sheet and any table are the last leaves in a description
       * that carries them, and neither is the prose: clamping a one-line
       * spec row leaves the paragraph uncut and hides the toggle, because
       * that row never overflows. The prose is what this clamps. */
      if (all[i].closest('table, .kp-spec-sheet')) continue;
      if (all[i].children.length === 0 && all[i].textContent.trim()) return all[i];
    }
    return null;
  }

  overflows() {
    return this.body.scrollHeight > this.body.clientHeight + 1;
  }

  /*
   * Renders the prose cut to n characters, exactly as it will be left.
   *
   * The tidying is part of the render, not a step after the search, and that
   * is load-bearing: a trimmed string wraps differently from the raw slice it
   * came from — drop two characters and the nowrap tail can lose its place on
   * the line and take a third one. Measuring anything but the final string
   * therefore proves nothing about the final string.
   *
   * The partial word only goes when the search actually stopped inside one —
   * testing the NEXT character is what tells the two cases apart, and without
   * it a cut that happened to land on a word end would eat that whole word.
   */
  apply(n) {
    let cut = this.full.slice(0, n);
    if (n < this.full.length && !/\s/.test(this.full[n])) {
      cut = cut.replace(/\S+$/, '');
    }
    const shown = cut.replace(/[\s.,;:!?—–-]+$/, '');
    this.textNode.data = `${shown} `;
    /* Sliced at the shown length, not at `n`, so the punctuation the tidying
     * dropped starts the remainder instead of vanishing between the two. */
    this.rest.textContent = this.full.slice(shown.length);
  }

  /*
   * Longest cut that still fits, found by halving. Measured with the tail in
   * place, so the cut mark and the toggle are part of what has to fit — that
   * is the difference between a toggle at the end of the last line and a
   * toggle that pushes the line it belongs to out of the clamp.
   *
   * `low` only ever advances to a length that has just been measured fitting,
   * so the closing apply() puts back a state already proven to fit.
   */
  fit() {
    if (this.open) return;

    this.tail.hidden = true;
    this.textNode.data = this.full;
    this.rest.textContent = '';
    if (!this.overflows()) return;

    this.tail.hidden = false;
    this.cut.hidden = false;

    let low = 0;
    let high = this.full.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      this.apply(mid);
      if (this.overflows()) {
        high = mid - 1;
      } else {
        low = mid;
      }
    }

    this.apply(low);
  }

  /* The search is a dozen forced reflows; a resize drag fires dozens of
   * events. One per frame is plenty and keeps the drag smooth. */
  schedule() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.fit();
    });
  }

  setOpen(open) {
    this.open = open;
    this.body.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.toggle.textContent = open ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;

    if (open) {
      this.textNode.data = `${this.full} `;
      this.rest.textContent = '';
      this.cut.hidden = true;
    } else {
      this.fit();
    }
  }
}

if (!customElements.get('kp-read-more')) {
  customElements.define('kp-read-more', KpReadMore);
}

/*
 * The gallery's bar row.
 *
 * Dawn moves `aria-current` across the thumbnails from its `slideChanged`
 * event, which SliderComponent only fires when it is driving the rail. The
 * PDP does not let it: on desktop the media list is a CSS grid, where slides
 * 1 and 2 share an offsetLeft, so Dawn's paging maths divides by zero and
 * hands MediaGallery an undefined slide. Its prev/next buttons are removed,
 * which makes SliderComponent bail out of its constructor for good.
 *
 * What is left to replace is one thing only: telling Dawn which media the
 * rail has landed on. The work itself is still Dawn's — this calls
 * MediaGallery.setActiveThumbnail, it does not reimplement it.
 */
class KpGalleryRail {
  constructor(gallery) {
    this.gallery = gallery;
    this.rail = gallery.querySelector('.product__media-list');
    this.thumbs = gallery.querySelector('.thumbnail-list');
    if (!this.rail || !this.thumbs) return;

    this.frame = null;
    this.rail.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    // The rail starts part-scrolled by its peek, so the bar needs a first read.
    this.sync();
  }

  onScroll() {
    // Coalesce a scroll burst into one measurement per frame.
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.sync();
    });
  }

  sync() {
    const slides = [...this.rail.children];
    if (!slides.length) return;

    // The fill runs ahead of the snapping, every frame, so the bar tracks the
    // finger rather than jumping a segment at a time. It measures how much of
    // the rail has been seen — clientWidth included, or the last photo could
    // be fully on screen with the bar still short of the end.
    const travel = this.rail.scrollWidth - this.rail.clientWidth;
    const seen = travel > 0 ? (this.rail.scrollLeft + this.rail.clientWidth) / this.rail.scrollWidth : 1;
    this.thumbs.style.setProperty('--kp-rail-progress', Math.min(1, Math.max(0, seen)).toFixed(4));

    // The snapped slide is whichever centre is nearest the rail's centre.
    const railMid = this.rail.scrollLeft + this.rail.clientWidth / 2;
    let nearest = slides[0];
    let best = Infinity;
    slides.forEach((slide) => {
      const mid = slide.offsetLeft + slide.offsetWidth / 2;
      const gap = Math.abs(mid - railMid);
      if (gap < best) {
        best = gap;
        nearest = slide;
      }
    });

    const mediaId = nearest.dataset.mediaId;
    if (!mediaId || mediaId === this.lastId) return;
    this.lastId = mediaId;

    const thumb = this.thumbs.querySelector(`[data-target="${mediaId}"]`);
    if (thumb && typeof this.gallery.setActiveThumbnail === 'function') {
      this.gallery.setActiveThumbnail(thumb);
    }
  }
}

// The marker, not KP_PDP_FIRST_RUN: the gallery the swap brought in DOES need
// a rail. What must not happen is a second rail on the outgoing gallery, which
// is still in the document while this runs.
document.querySelectorAll('media-gallery:not([data-kp-rail])').forEach((gallery) => {
  gallery.setAttribute('data-kp-rail', '');
  new KpGalleryRail(gallery);
});

/*
 * The autoplaying gallery video.
 *
 * `autoplay` on the tag is not enough on its own. Dawn's global
 * pauseAllMedia() stops every <video> on the page whenever the active media
 * changes, a modal opens, or a variant swaps — so the loop dies on the first
 * interaction and never comes back. An IntersectionObserver is what restores
 * it, and it earns its keep twice over: a product video that has scrolled
 * out of the mosaic has no business decoding frames.
 *
 * Under prefers-reduced-motion nothing is played at all. The <video> carries
 * a poster, so the frame is still there — §20's rule is that content stays
 * visible, not that it moves.
 */
const KP_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

function kpWatchGalleryVideos() {
  // Same reasoning as the gallery rail: the film the swap brought in needs
  // watching, the outgoing page's already is. Without the marker every swap
  // stacks another pair of IntersectionObservers and another pause→play
  // handler on each well, and the pause handler is the one that hurts — N
  // copies race to call play() on the same paused video.
  if (KP_REDUCED_MOTION.matches) return;

  const wells = [...document.querySelectorAll('.product__media--autoplay:not([data-kp-video-watch])')];
  if (!wells.length) return;
  wells.forEach((well) => well.setAttribute('data-kp-video-watch', ''));

  const inView = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          inView.add(entry.target);
          // Muted autoplay can still be refused. The poster stays, and the
          // well says so, so a tap can start it (#44).
          video.play().catch((error) => {
            if (error && error.name === 'AbortError') return;
            entry.target.classList.add('is-blocked');
          });
        } else {
          inView.delete(entry.target);
          if (!video.paused) video.pause();
        }
      });
    },
    { threshold: 0.25 }
  );

  // One screen ahead (#43): the header and first bytes come in before the
  // well is seen, so play() has something to start from. Once per well.
  const near = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const video = entry.target.querySelector('video');
        if (video && video.preload === 'none') video.preload = 'metadata';
        near.unobserve(entry.target);
      });
    },
    { rootMargin: '100% 0px' }
  );

  wells.forEach((well) => {
    observer.observe(well);
    near.observe(well);

    const video = well.querySelector('video');
    if (!video) return;

    /*
     * A refused play (#44): iOS Low Power Mode and Android Data Saver turn
     * down scripted autoplay. The well marks itself so CSS shows a play mark,
     * and a tap on it plays inside the gesture — a tap, not a drag: the well
     * is inside the gallery's swipe surface.
     */
    video.addEventListener('playing', () => well.classList.remove('is-blocked'));
    let from = null;
    well.addEventListener('pointerdown', (event) => {
      from = { x: event.clientX, y: event.clientY };
    }, { passive: true });
    well.addEventListener('pointerup', (event) => {
      const start = from;
      from = null;
      if (!well.classList.contains('is-blocked')) return;
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
      video.play().catch(() => {});
    });

    /*
     * The observer alone is not enough. It only fires when intersection
     * CHANGES, so a video already on screen when pauseAllMedia() runs — the
     * usual case: open the lightbox, close it again without scrolling — stays
     * frozen with nothing left to wake it. Listening for the pause itself is
     * what closes that gap.
     *
     * `ended` guards a non-looping video, and `inView` is what keeps this from
     * fighting the observer's own deliberate pause.
     */
    video.addEventListener('pause', () => {
      if (!inView.has(well) || video.ended) return;
      video.play().catch(() => {});
    });
  });
}

// After `load`, not at parse: on a slow link the gallery film otherwise
// competes with the buy box's CSS, fonts and JS for the first seconds (#26).
if (document.readyState === 'complete') {
  kpWatchGalleryVideos();
} else {
  window.addEventListener('load', kpWatchGalleryVideos, { once: true });
}

/*
 * The sticky add to cart bar.
 *
 * Two jobs, and neither of them is adding to the cart — the bar's button is
 * a plain submit pointing `form=` at Dawn's product form, so the click is
 * Dawn's from the moment it happens (hard rule 2).
 *
 *   1. Show the bar once the real buy button has scrolled ABOVE the viewport.
 *      "Out of view" alone is not the test: on mobile the buy box sits below
 *      a full-height gallery, so the button is off screen at the top of the
 *      page too — and a sticky bar that greets you before you have seen the
 *      price is an ad, not a shortcut.
 *
 *      Past that point it stays, whichever way the page moves, and steps
 *      aside only for the footer — see connectedCallback for the conditions.
 *
 *   2. Keep up with the variant picker. Dawn already fetches the whole
 *      section on a variant change and hands it to subscribers, and that
 *      render contains a fresh copy of this bar. Swapping ours for that one
 *      updates the thumb, the summary and the price in a single move — and
 *      the money stays formatted by Liquid, which is the only place the
 *      shop's currency format actually lives.
 *
 * The button's own state does NOT come from that event, and this is the part
 * that bites. When a customer picks a combination no variant exists for,
 * product-info.js calls setUnavailable() and returns BEFORE it publishes
 * variantChange — so a bar listening only to that event would keep the last
 * variant's price beside an enabled ADD TO CART, while Dawn has already
 * blanked the form's `id` input. Clicking it would POST an empty id and land
 * the error in the buy box, off screen.
 *
 * So the button mirrors Dawn's button instead of guessing: a MutationObserver
 * on the button, and the label copied across with it. Every path Dawn has for
 * disabling — sold out, unavailable, quantity rules — arrives through the
 * `disabled` attribute, whether or not an event went with it.
 *
 *   3. Show what the real button is doing while an add is in flight. Dawn's
 *      product-form.js finds its button and its spinner with
 *      `this.querySelector`, scoped INSIDE <product-form>, and this button is
 *      outside it — so Dawn sets `loading` and un-hides the spinner on a
 *      button that has been scrolled past. The same observer mirrors that
 *      across: the `loading` class, `aria-disabled` while busy (never the
 *      `disabled` attribute, which would drop the button out of the
 *      accessibility tree mid-request), and the label/spinner swap. It adds
 *      no behaviour: Dawn's own aria-disabled guard is what blocks a double
 *      submit, with or without this.
 *
 * This used to be split across two files, and the second one cached the
 * label span and the spinner at construction — nodes that onVariantChange()
 * below throws away with every innerHTML swap, so the loading state went
 * silent after the first variant change (#41, fix 1). One owner now, and
 * nothing inside the element is cached across a render.
 */
class KpStickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.id.replace('KpSticky-', '');

    this.anchor = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (!this.anchor) return;

    /*
     * In when the button is off screen AND the price has already been seen.
     * "Seen" is the price row having scrolled up past the top: on a phone the
     * buy button sits a full screen under the price, and a customer reading
     * the highlights with no button in reach is exactly who the bar is for.
     * Before the price it stays away — a bar that greets you first is an ad.
     */
    /*
     * Shown when both hold — the panel is a shortcut to a button the
     * shopper has seen and can no longer reach, offered only while it is
     * not in the way:
     *
     *   armed     the buy button is above the viewport AND the price row
     *             has scrolled past. On a phone the button sits a screen
     *             under the price, so "button off screen" alone is true at
     *             the top of the page too — and a panel that greets you
     *             before the price is an ad.
     *   clear     the footer is not under where the panel sits. The footer
     *             is the one thing the panel must never cover: it holds
     *             the legal row and the payment marks, and there is nothing
     *             left to buy from down there. Watched with a root shrunk
     *             by the panel's footprint, so the panel steps aside the
     *             moment it would start covering footer content — which is
     *             also why the body needs no bottom padding any more.
     *
     * The scroll direction is not a condition. The panel used to hide while
     * the page moved down and come back only on a scroll up; the owner wants
     * it on screen the whole way down once the button has gone (2026-09-25).
     * kp-back-to-top keeps its own down-hides rule.
     */
    this.buttonAway = false;
    this.pricePast = false;
    this.footerUnder = false;
    // While the price ladder shows (kp-pdp-ladder.liquid) the ladder is the
    // price, and Dawn's price row is display:none. A box that is not rendered
    // never intersects, so watching the row would set pricePast back to
    // false on a page loaded already scrolled past the button.
    const price =
      document.getElementById(`KpLadder-${this.sectionId}`) || document.getElementById(`price-${this.sectionId}`);
    this.update = () => this.classList.toggle('kp-sticky--in', this.buttonAway && this.pricePast && !this.footerUnder);

    this.visibility = new IntersectionObserver(
      ([entry]) => {
        this.buttonAway = !entry.isIntersecting;
        if (entry.boundingClientRect.top < 0) this.pricePast = true;
        this.update();
      },
      { threshold: 0 }
    );
    this.visibility.observe(this.anchor);

    if (price) {
      this.priceWatch = new IntersectionObserver(
        ([entry]) => {
          this.pricePast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.update();
        },
        { threshold: 0 }
      );
      this.priceWatch.observe(price);
    } else {
      this.pricePast = true;
    }

    this.footer = document.querySelector('.footer');
    this.watchFooter();

    /*
     * One observe() call, not two: registering the same node twice REPLACES
     * the first set of options rather than adding to them, which would have
     * quietly dropped the attribute watch. Dawn writes the label into a child
     * span and the disabled flag onto the button, so both have to be in here.
     */
    this.state = new MutationObserver(() => this.mirrorButton());
    this.state.observe(this.anchor, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }

    /*
     * The body gets the panel's height back as bottom padding, and the
     * back-to-top button is lifted by it (kp-sticky-atc.css). The sheet
     * carries a fallback, but the real height depends on the fonts that
     * loaded and the length of the second line, so it is measured here and
     * written to :root, where both readers can see it. Observed on the
     * element itself, not the panel inside it: the panel is replaced on
     * every variant change and the element is not.
     */
    if ('ResizeObserver' in window) {
      this.size = new ResizeObserver(() => {
        const panel = this.querySelector('.kp-sticky__panel');
        if (!panel) return;
        document.documentElement.style.setProperty('--kp-sticky-h', `${panel.offsetHeight}px`);
        // The footprint is the footer watcher's root margin, so it follows.
        this.watchFooter();
      });
      this.size.observe(this);
    }

    this.mirrorButton();
  }

  disconnectedCallback() {
    this.visibility?.disconnect();
    this.priceWatch?.disconnect();
    this.footerWatch?.disconnect();
    this.state?.disconnect();
    this.size?.disconnect();
    this.unsubscribe?.();
  }

  /*
   * Footprint = the panel's height plus its inset off the bottom, read from
   * the same custom properties the CSS positions it with. The observer's
   * root is shrunk by that much, so "intersecting" means "the footer's top
   * has reached the panel", not "the footer is on screen".
   */
  watchFooter() {
    if (!this.footer) return;
    const style = getComputedStyle(document.documentElement);
    const footprint = Math.round(
      parseFloat(style.getPropertyValue('--kp-sticky-h')) + parseFloat(getComputedStyle(this).bottom)
    );
    if (!footprint || footprint === this.footprint) return;
    this.footprint = footprint;

    this.footerWatch?.disconnect();
    this.footerWatch = new IntersectionObserver(
      ([entry]) => {
        this.footerUnder = entry.isIntersecting;
        this.update();
      },
      { threshold: 0, rootMargin: `0px 0px -${footprint}px 0px` }
    );
    this.footerWatch.observe(this.footer);
  }

  // Queried every time, never cached: onVariantChange() replaces the button.
  // (Restored: it went missing with the idle timer in 3de4de0, and without
  // it mirrorButton() returned before mirroring anything.)
  get submit() {
    return this.querySelector('.kp-sticky__submit');
  }

  mirrorButton() {
    const submit = this.submit;
    if (!submit || !this.anchor) return;

    submit.disabled = this.anchor.hasAttribute('disabled');

    const loading = this.anchor.classList.contains('loading');
    const busy = this.anchor.getAttribute('aria-disabled') === 'true';
    submit.classList.toggle('loading', loading);
    if (busy) submit.setAttribute('aria-disabled', 'true');
    else submit.removeAttribute('aria-disabled');

    // Queried here, never cached: onVariantChange() replaces every node inside
    // the element, and a reference taken earlier would point at a node that
    // is no longer in the document.
    //
    // Into the span, never onto the button: `submit.textContent = label` wipes
    // out every child, spinner included.
    // Dawn's label is mirrored only while Dawn has disabled the button
    // ("Sold out", "Unavailable"). Enabled, the bar keeps the label Liquid
    // gave it (data-kp-own-label) — on a pre-order that is the short
    // "PRE-ORDER", where the buy box carries the whole ship window.
    const target = submit.querySelector('span');
    const label = submit.disabled
      ? this.anchor.querySelector('span')?.textContent.trim()
      : target?.dataset.kpOwnLabel;
    if (label && target) target.textContent = label;

    // `hidden` is Dawn's class and carries !important, which is why the label
    // and the bars are swapped with it rather than with a style of our own.
    target?.classList.toggle('hidden', loading);
    submit.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  onVariantChange(event) {
    const html = event?.data?.html;
    if (!html) return;

    const fresh =
      html.getElementById(`KpSticky-${event.data.sectionId}`) || html.querySelector('[data-kp-sticky]');
    if (!fresh) return;

    // innerHTML only: replacing the element itself would drop the class the
    // observer just set, and flash the bar back down mid-scroll.
    this.innerHTML = fresh.innerHTML;

    // The fresh render is the section as Liquid saw it before Dawn's own JS
    // had its say, so the mirrored state is re-applied on top of it.
    this.mirrorButton();
  }
}

if (!customElements.get('kp-sticky-atc')) {
  customElements.define('kp-sticky-atc', KpStickyAtc);
}

/*
 * The price ladder's pick (snippets/kp-pdp-ladder.liquid).
 *
 * The ladder is a radio group and nothing in it touches the cart. This
 * element is what joins the pick to the one add to cart, and it does three
 * things:
 *
 *   1. Writes the picked row's price into Dawn's Add to cart button, after
 *      its label, and tells the sticky bar which price to show (data-tier on
 *      <kp-sticky-atc>; both prices are in its markup, formatted by Liquid).
 *      The price is what the cart will hold NOW: with the text row picked it
 *      is still the code price, because the text code comes back by SMS.
 *
 *   2. Holds Dawn's submit for as long as the cart takes to match the pick
 *      (kp-copy-code.js, the ADR 0001 shape), then lets the same submit go:
 *      window.kpApplyCode when the pick needs the store code,
 *      window.kpRemoveCode when it is full price. The add itself, the drawer
 *      and the errors stay Dawn's (hard rule 2). Neither call rejects and the
 *      add goes ahead whatever they say: a cue in the cart at the wrong price
 *      is a smaller failure than a button that does nothing, and the drawer
 *      shows the code and its box either way.
 *
 *      It asks every time and keeps no memory of the cart. It used to
 *      remember that the code was on, skip the call and lock the full-price
 *      row (2026-10-05, for a few hours): a code taken off in the drawer, or
 *      left on an emptied cart, then made the row unpickable or the price on
 *      the button wrong. A code the cart already has costs one request and
 *      changes nothing.
 *
 *      The listener is on the document, in the capture phase, so it runs
 *      before Dawn's own on the form. Both the buy box's button and the
 *      sticky bar's submit that form, so both come through here.
 *
 * Without this script the page still works as a shop: the rows pick, the
 * button says Add to cart, and it adds the cue at whatever the cart makes it.
 */
class KpLadderPick extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    this.vip();
    this.code = String(this.dataset.code || '').trim().toUpperCase();

    // The receipt layout's two switches: the text saving stacks on the store
    // code, so the text brings the code with it and the code takes the text
    // away. (The pick layout is one radio group and needs no such rule.)
    this.onChange = (event) => {
      const code = this.querySelector('input[type="checkbox"][value="code"]');
      const text = this.querySelector('input[type="checkbox"][value="text"]');
      if (code && text) {
        if (event.target === text && text.checked) code.checked = true;
        if (event.target === code && !code.checked) text.checked = false;
      }
      this.sync();
    };
    this.onSubmit = (event) => this.hold(event);
    this.addEventListener('change', this.onChange);
    document.addEventListener('submit', this.onSubmit, true);
    this.sync();
  }

  /*
   * A subscriber who came through the kyperus.com/discount/CODE link of the
   * early-access subscriber offer: Shopify left that code in the
   * discount_code cookie. The receipt then shows that code and its price
   * (printed by Liquid as data-vip-*), and the button applies it instead of
   * the public one. Anyone else sees the receipt exactly as rendered.
   */
  vip() {
    const want = String(this.dataset.vipCode || '').trim().toUpperCase();
    if (!want) return;
    const raw = document.cookie.split('; ').find((c) => c.startsWith('discount_code='));
    const has = raw ? decodeURIComponent(raw.slice(14)).split(',').map((c) => c.trim().toUpperCase()) : [];
    if (!has.includes(want)) return;
    const d = this.dataset;
    d.code = want;
    d.priceCode = d.vipPrice;
    const line = this.querySelector('.kp-pdp-receipt__line--switch');
    const set = (el, html) => { if (el && html) el.innerHTML = html; };
    set(line?.querySelector('.kp-pdp-ladder__label'), d.vipLabel);
    set(line?.querySelector('.kp-pdp-receipt__amt--off'), d.vipMinus);
    set(line?.querySelector('.kp-pdp-ladder__persona'), d.vipPersona);
    this.querySelectorAll('.kp-pdp-ladder__save [data-kp-total="code"]').forEach((el) => set(el, d.vipSave));
    this.querySelectorAll('.kp-pdp-receipt__sum [data-kp-total="code"]').forEach((el) => set(el, d.vipTotal));
    document.querySelectorAll('kp-sticky-atc .kp-sticky__tier--code').forEach((tier) => {
      const chip = tier.querySelector('.kp-sticky__chip');
      tier.querySelector('.kp-price')?.remove();
      tier.insertAdjacentHTML('afterbegin', d.vipSticky);
      if (chip) chip.textContent = want;
    });
    // The subscriber code combines with nothing but shipping (owner,
    // 2026-10-29), so the text saving is not theirs to stack: the row, its
    // strip and its totals go.
    this.querySelector('input[value="text"]')?.closest('label')?.remove();
    this.querySelectorAll('.kp-pdp-ladder__more, [data-kp-total="text"]').forEach((el) => el.remove());
    this.classList.add('is-vip');
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('submit', this.onSubmit, true);
  }

  // Looked up each time: Dawn replaces the button's node on a product swap.
  get button() {
    return document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
  }

  // Either layout: a pick that needs the store code has its "code" or its
  // "text" input checked (the radio of that row, or the receipt's switch).
  get needsCode() {
    return Boolean(this.querySelector('input[value="code"]:checked, input[value="text"]:checked'));
  }

  sync() {
    const price = this.needsCode ? this.dataset.priceCode : this.dataset.priceFull;
    const button = this.button;
    if (button && price) {
      let slot = button.querySelector('[data-kp-atc-price]');
      if (!slot) {
        slot = document.createElement('span');
        slot.className = 'kp-atc-price';
        slot.setAttribute('data-kp-atc-price', '');
        // After Dawn's label span, which product-info.js finds as the first
        // span in the button and rewrites; this one it never touches.
        (button.querySelector('span') || button).after(slot);
      }
      if (slot.textContent !== price) slot.textContent = price;
    }
    document.querySelectorAll('kp-sticky-atc').forEach((bar) => {
      bar.dataset.tier = this.needsCode ? 'code' : 'full';
    });
  }

  hold(event) {
    if (event.target !== this.form || this.passing || !this.code) return;
    const settle = this.needsCode ? window.kpApplyCode : window.kpRemoveCode;
    if (typeof settle !== 'function') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.holding) return;
    this.holding = true;

    const submitter = event.submitter;
    const button = this.button;
    // Dawn's own waiting state, a moment early: the tap has an answer before
    // the cart does. Dawn's submit sets the same three things again.
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
    }

    Promise.resolve(settle(this.code))
      .catch(() => false)
      .then(() => {
        this.holding = false;
        // Dawn's handler returns at once on aria-disabled, so it comes off
        // before the submit is handed back.
        button?.removeAttribute('aria-disabled');
        if (!this.form?.isConnected) return;
        this.passing = true;
        try {
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit(submitter && submitter.form === this.form ? submitter : undefined);
          } else {
            this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } finally {
          this.passing = false;
        }
      });
  }
}

if (!customElements.get('kp-ladder-pick')) {
  customElements.define('kp-ladder-pick', KpLadderPick);
}

/*
 * The pre-order panel (#60), kept true across a variant change.
 *
 * It is Liquid: whether the selected variant is a pre-order is decided
 * server-side in snippets/kp-preorder.liquid, and nothing here re-derives
 * it. What this element does is the same move as the sticky bar above: Dawn
 * fetches the whole section on a variant change and hands the render to
 * subscribers, so the panel is swapped for the fresh copy, hidden state
 * included.
 *
 * The button label is the one extra step. product-info.js re-enables the
 * submit button with `toggleSubmitButton(false)`, which writes
 * variantStrings.addToCart into the label span — over the "PRE-ORDER · SHIPS
 * SEP 25" that Liquid rendered for the new variant. So the label is copied
 * back out of the fresh render, into the span only (never the button, whose
 * other children are the busy word and the spinner), and only while the
 * button is enabled: a disabled button's label is Dawn's ("Sold out",
 * "Unavailable") and stays Dawn's. The sticky bar mirrors the button through
 * its own observer, so it follows without being told.
 */
class KpPreorder extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.id.replace('KpPreorder-', '');
    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  // Copy one element's content and hidden state out of the fresh render.
  static swapFrom(html, id) {
    const live = document.getElementById(id);
    const fresh = html.getElementById(id);
    if (!live || !fresh) return;
    live.innerHTML = fresh.innerHTML;
    live.hidden = fresh.hidden;
  }

  onVariantChange(event) {
    const html = event?.data?.html;
    if (!html) return;

    KpPreorder.swapFrom(html, this.id);

    const buttonId = `ProductSubmitButton-${this.sectionId}`;
    const button = document.getElementById(buttonId);
    const freshButton = html.getElementById(buttonId);
    if (!button || !freshButton || button.hasAttribute('disabled')) return;
    const label = freshButton.querySelector('span')?.textContent.trim();
    const target = button.querySelector('span');
    if (label && target) target.textContent = label;
  }
}

if (!customElements.get('kp-preorder')) {
  customElements.define('kp-preorder', KpPreorder);
}

/*
 * The lightbox, patched in two places. Both are Dawn defects that only show up
 * because our lightbox is a single centred photo rather than Dawn's scrolling
 * list — the JS is left alone and wrapped from here, per hard rule 1.
 *
 * 1. The page moved while you weren't looking. product-modal.js ends
 *    showActiveMedia() with `activeMedia.scrollIntoView()`, meant to bring the
 *    clicked photo into Dawn's scrolling modal. Ours has nothing to scroll, so
 *    the call walks up and scrolls the DOCUMENT instead. You cannot see it
 *    happen — the scrim is covering the page — so it reads as the page jumping
 *    on close. Pinning scrollY across open and close makes it a no-op.
 *
 * 2. Tapping the scrim did nothing on touch. ModalDialog's dismiss listener is
 *    `pointerType === 'mouse'` only, to protect pinch-zoom, so a phone had no
 *    way out but the X. We close on a scrim tap that did not travel — a pinch
 *    or a drag moves the pointer and is left alone.
 */
customElements.whenDefined('product-modal').then(() => {
  const proto = customElements.get('product-modal').prototype;
  if (proto.kpScrollPinned) return;
  proto.kpScrollPinned = true;

  const show = proto.show;
  const hide = proto.hide;

  proto.show = function (opener) {
    // A close still counting down is cancelled outright. Without this, tapping
    // a second photo inside the 300ms window left kpClosing stuck true — so
    // Escape and the X silently did nothing — and then the stale timer fired
    // and closed the modal the shopper had just opened and was looking at.
    clearTimeout(this.kpCloseTimer);
    this.kpClosing = false;
    this.classList.remove('kp-modal-closing');

    this.kpScrollY = window.scrollY;
    show.call(this, opener);
    window.scrollTo(window.scrollX, this.kpScrollY);
    kpSyncModal(this);
  };

  proto.hide = function () {
    if (this.kpClosing) return;

    const finish = () => {
      this.kpClosing = false;
      this.classList.remove('kp-modal-closing');
      const y = this.kpScrollY;
      hide.call(this);
      // After hide(), not inside it: removeTrapFocus() focuses the opener, and
      // focusing scrolls too.
      if (y != null) window.scrollTo(window.scrollX, y);
    };

    // hide() sets display:none on the spot, so the scrim has to be given its
    // fade before the real close rather than during it. 300ms is --kp-dur-fast.
    if (KP_REDUCED_MOTION.matches) {
      finish();
      return;
    }
    this.kpClosing = true;
    this.classList.add('kp-modal-closing');
    this.kpCloseTimer = setTimeout(finish, 300);
  };
});

/*
 * Stepping through the lightbox. The mosaic stays the place you browse, but
 * once a photo is open the obvious next move is the next photo, so the
 * lightbox gets arrows, arrow keys and a swipe.
 *
 * It moves the SAME .active class product-modal.js sets, on the same nodes
 * product-media-modal.liquid already renders, so there is no second viewer
 * here — just a different way to choose which of Dawn's media is showing.
 */
function kpModalMedia(modal) {
  return Array.from(modal.querySelectorAll('.product-media-modal__content > [data-media-id]'));
}

/*
 * Everything that has to be true of the lightbox once .active has moved:
 * the counter reads right, the loader covers a photo that has not arrived,
 * and the photos on either side start downloading before they are asked for.
 */
function kpSyncModal(modal) {
  const media = kpModalMedia(modal);
  if (!media.length) return;

  const index = media.findIndex((el) => el.classList.contains('active'));
  if (index < 0) return;

  const readout = modal.querySelector('[data-kp-modal-index]');
  if (readout) readout.textContent = String(index + 1).padStart(2, '0');

  // The neighbours, so a step lands on a photo the browser already has. Only
  // the neighbours: making all nine eager would pull megabytes on page load,
  // which is the reason Dawn marked them lazy in the first place.
  [-1, 0, 1].forEach((offset) => {
    const img = media[(index + offset + media.length) % media.length];
    if (img?.nodeName === 'IMG') img.loading = 'eager';
  });

  const loader = modal.querySelector('.product-media-modal__loader');
  const frame = modal.querySelector('.product-media-modal__content');

  clearTimeout(modal.kpLoaderTimer);
  loader?.classList.add('hidden');

  const active = media[index];
  const photo = active.nodeName === 'IMG' ? active : active.querySelector('img');

  // The frame is a 1px accent edge around whatever it holds. A photo that has
  // not loaded gives it nothing to hold, so the whole frame collapses to a
  // 2x2px square of pure --kp-border-accent in the middle of a black screen —
  // the orange blink between two photos. The edge waits for something to draw
  // around. (Stepping avoids this outright; this covers the first open, which
  // is product-modal.js's to drive, not ours.)
  frame?.classList.toggle('is-waiting', !!photo && !photo.complete);

  if (!loader || !photo || photo.complete) return;

  // Held back: a photo the browser already has decodes inside a frame, and a
  // loader that appears and disappears in that time is just a flash of orange.
  modal.kpLoaderTimer = setTimeout(() => loader.classList.remove('hidden'), 180);

  const done = () => {
    clearTimeout(modal.kpLoaderTimer);
    loader.classList.add('hidden');
    frame?.classList.remove('is-waiting');
  };
  photo.addEventListener('load', done, { once: true });
  photo.addEventListener('error', done, { once: true });
}

function kpShowModalMedia(modal, next) {
  kpModalMedia(modal).forEach((el) => el.classList.remove('active'));
  next.classList.add('active');

  // Restarting the fade: a class the browser has never dropped animates only
  // once, so it comes off and goes back on across a reflow.
  next.classList.remove('kp-modal-enter');
  void next.offsetWidth;
  next.classList.add('kp-modal-enter');

  // Dawn defers YouTube until the media is shown; stepping onto one has to
  // ask for it the same way showActiveMedia() does.
  const template = next.querySelector('template');
  if (next.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) {
    next.loadContent();
  }

  window.pauseAllMedia?.();
  kpSyncModal(modal);
}

/*
 * The photo you are looking at stays until the next one can replace it.
 *
 * Swapping first and waiting after is what produced the blink: the outgoing
 * photo is display:none'd, the incoming one has no bytes yet and therefore no
 * size, and the frame — a 1px accent edge around its content — collapses to a
 * 2px orange square, then a loader, then finally the photograph. Three states
 * for one step. Holding the old photo makes it one.
 *
 * The token guards the race: press next twice quickly and the first load
 * resolving must not drag the view back a slide.
 */
function kpStepModal(modal, direction) {
  const media = kpModalMedia(modal);
  if (media.length < 2) return;

  const current = media.findIndex((el) => el.classList.contains('active'));
  const next = media[(current + direction + media.length) % media.length];
  if (!next || next === media[current]) return;

  const token = (modal.kpStepToken || 0) + 1;
  modal.kpStepToken = token;

  const photo = next.nodeName === 'IMG' ? next : next.querySelector('img');
  if (!photo || photo.complete) {
    kpShowModalMedia(modal, next);
    return;
  }

  // Lazy is right for nine 4096px photographs on a page nobody has opened
  // yet; it is wrong for the one being asked for right now.
  photo.loading = 'eager';

  const loader = modal.querySelector('.product-media-modal__loader');
  clearTimeout(modal.kpLoaderTimer);
  if (loader) {
    modal.kpLoaderTimer = setTimeout(() => {
      if (modal.kpStepToken === token) loader.classList.remove('hidden');
    }, 180);
  }

  const go = () => {
    if (modal.kpStepToken !== token) return;
    clearTimeout(modal.kpLoaderTimer);
    loader?.classList.add('hidden');
    kpShowModalMedia(modal, next);
  };
  photo.addEventListener('load', go, { once: true });
  photo.addEventListener('error', go, { once: true });
}

/*
 * The lightbox's five delegated listeners, registered ONCE per document.
 *
 * They match on the event's target, so they already reach the DOM a swap
 * brings in — nothing here needs re-registering, and re-registering is
 * actively wrong: with two copies live, one ArrowRight steps the gallery
 * twice, three after two swaps. Measured before the guard: keydown listeners
 * on `document` went 0 → 3 → 7 across two swaps.
 */
if (KP_PDP_FIRST_RUN) {
document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-kp-modal-step]');
  if (!step) return;
  const modal = step.closest('.product-media-modal');
  if (modal) kpStepModal(modal, Number(step.dataset.kpModalStep));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
  const modal = document.querySelector('.product-media-modal[open]');
  if (!modal) return;
  event.preventDefault();
  kpStepModal(modal, event.key === 'ArrowRight' ? 1 : -1);
});

document.addEventListener('pointerdown', (event) => {
  const modal = event.target.closest('.product-media-modal');
  if (!modal) return;
  modal.kpPointerFrom = { x: event.clientX, y: event.clientY };
});

// A gesture the browser took for itself is over. Without this its start point
// survives to be measured against the NEXT release, which is a different
// gesture entirely.
document.addEventListener('pointercancel', (event) => {
  const modal = event.target.closest('.product-media-modal');
  if (modal) modal.kpPointerFrom = null;
});

/* One capture-phase handler owns every pointer release inside the lightbox,
   because it has to run BEFORE ModalDialog's own dismiss listener on the
   modal — that is what lets the photograph stop being a close button without
   editing global.js. */
document.addEventListener(
  'pointerup',
  (event) => {
    const modal = event.target.closest('.product-media-modal');
    if (!modal || typeof modal.hide !== 'function') return;

    const from = modal.kpPointerFrom;
    modal.kpPointerFrom = null;
    const dx = from ? event.clientX - from.x : 0;
    const dy = from ? event.clientY - from.y : 0;
    const travelled = Math.hypot(dx, dy);

    // A video is rendered with native controls and a model orbits on drag, so
    // inside either of those a horizontal drag is THEIR gesture: scrubbing the
    // timeline, or turning the cue. Stepping the lightbox out from under it
    // would be indistinguishable from a bug.
    const ownsTheDrag = event.target.closest('deferred-media, product-model, video, model-viewer, button');

    // A horizontal drag across the photo steps it. Touch only: a mouse has
    // the arrows and the arrow keys, and a mouse drag is usually a selection.
    if (!ownsTheDrag && event.pointerType !== 'mouse' && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      event.stopPropagation();
      kpStepModal(modal, dx < 0 ? 1 : -1);
      return;
    }

    // Clicking the photograph used to close the lightbox — harmless when the
    // lightbox was one still image, a trap now that there are arrows beside it.
    if (event.target.closest('.product-media-modal__content, .product-media-modal__step, deferred-media, product-model, button')) {
      event.stopPropagation();
      return;
    }

    // Left: the scrim. ModalDialog closes on it for a mouse; its listener is
    // pointerType === 'mouse' only, to protect pinch-zoom, so touch had no way
    // out but the X. A tap that did not travel is not a pinch.
    if (event.pointerType !== 'mouse' && travelled <= 10) modal.hide();
  },
  true
);
}

/*
 * On touch, the buy box's buttons turn white while pressed, and the press is
 * :active (kp-pdp-blocks.css, kp-main-product.css). iOS Safari paints :active
 * only on a page that listens for touches; an empty passive listener, once
 * per document, is the switch.
 */
if (KP_PDP_FIRST_RUN) {
  document.addEventListener('touchstart', () => {}, { passive: true });
}
})();
